import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { consultationsApi, patientsApi } from '../lib/api';

const LANGUAGES = [
  { value: 'en',  label: 'English' },
  { value: 'ha',  label: 'Hausa' },
  { value: 'yo',  label: 'Yoruba' },
  { value: 'ig',  label: 'Igbo' },
  { value: 'ikw', label: 'Ikwerre' },
  { value: 'twi', label: 'Twi (Akan)' },
  { value: 'gaa', label: 'Ga' },
  { value: 'ee',  label: 'Ewe' },
  { value: 'pcm', label: 'Nigerian Pidgin' },
  { value: 'fr',  label: 'French' },
  { value: 'sw',  label: 'Swahili' },
];

const LIMITED_TRANSCRIPTION_LANGS = new Set(['twi', 'ikw', 'gaa', 'ee']);

export default function NewConsultationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledPatientId = searchParams.get('patientId');

  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: searchResults } = useQuery({
    queryKey: ['patients-search', patientSearch],
    queryFn: () => patientsApi.list({ search: patientSearch, limit: 8 }),
    enabled: patientSearch.length > 1 && !selectedPatient,
  });

  // Auto-load prefilled patient
  useQuery({
    queryKey: ['patient', prefilledPatientId],
    queryFn: () => patientsApi.get(prefilledPatientId),
    enabled: !!prefilledPatientId && !selectedPatient,
    onSuccess: setSelectedPatient,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return setError('Please select a patient');
    if (!chiefComplaint.trim()) return setError('Chief complaint is required');

    setLoading(true);
    setError('');
    try {
      const consultation = await consultationsApi.create({
        patientId: selectedPatient.id,
        chiefComplaint: chiefComplaint.trim(),
        language,
      });
      navigate(`/app/consultations/${consultation.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create consultation');
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl">
      <button onClick={() => navigate(-1)} className="btn-ghost mb-6 -ml-2">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-semibold text-slate-800 mb-6">New Consultation</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Patient selection */}
        <div>
          <label className="label">Patient *</label>
          {selectedPatient ? (
            <div className="flex items-center justify-between p-3 rounded-lg border border-primary-500 bg-primary-50">
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {selectedPatient.lastName}, {selectedPatient.firstName}
                </p>
                <p className="text-xs text-clinical-muted font-mono">#{selectedPatient.patientNumber}</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                className="input"
                placeholder="Search by name or patient number…"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
              />
              {searchResults?.patients?.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-clinical-border rounded-lg shadow-lg overflow-hidden">
                  {searchResults.patients.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setSelectedPatient(p); setPatientSearch(''); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-slate-800">{p.lastName}, {p.firstName}</p>
                      <p className="text-xs text-clinical-muted font-mono">#{p.patientNumber}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chief complaint */}
        <div>
          <label className="label" htmlFor="complaint">Chief Complaint *</label>
          <input
            id="complaint"
            type="text"
            className="input"
            placeholder="e.g. Fever and headache for 3 days"
            value={chiefComplaint}
            onChange={(e) => setChiefComplaint(e.target.value)}
            required
          />
        </div>

        {/* Language */}
        <div>
          <label className="label" htmlFor="language">Consultation Language</label>
          <select
            id="language"
            className="input"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          <p className="text-xs text-clinical-muted mt-1">
            Select the primary language used in the consultation for better transcription and note accuracy.
          </p>
          {LIMITED_TRANSCRIPTION_LANGS.has(language) && (
            <p className="text-xs text-amber-600 mt-1">
              Transcription accuracy may be limited for this language. Notes will still be generated from available audio context.
            </p>
          )}
        </div>

        <div className="pt-2">
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Creating…' : 'Start Consultation'}
          </button>
        </div>
      </form>
    </div>
  );
}
